import React, { useRef, useState, useEffect } from 'react';
import { Square, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BoxOverlay } from '@/components/ui/BoxOverlay';
import { FloorplanMessageThread } from './FloorplanMessageThread';
import { FloorplanRebuildSuggestionModal } from './FloorplanRebuildSuggestionModal';
import { FloorplanUncertainHint } from './FloorplanUncertainHint';
import { FloorplanRefineInspectorPanel } from './FloorplanRefineInspectorPanel';
import { triggerFloorplanRebuild } from '@/services/floorplanService';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import type { BoxCoordinates, FloorplanMessage } from '@/types';
import type { IntentClassification } from '@/types/floorplanClassification';

// Pricing v4: rebuilds are regenerations of an already-paid floorplan → free
const REBUILD_COST_CREDITS_FALLBACK = 0;
const REBUILD_REASON_INTRO_FALLBACK = 'Wir haben festgestellt: ';

export interface FloorplanSendOptions {
  /**
   * Admin-only prompt-inspector escape hatch: skips prompt assembly and sends
   * this exact text to the image model. Set ONLY by FloorplanRefineInspectorPanel
   * (admin-gated) — never by the regular chat input.
   */
  finalPromptOverride?: string;
}

interface FloorplanFeintuningPaneProps {
  currentStateUrl: string;
  messages: FloorplanMessage[];
  activeMessageNumber: number | null;
  isGenerating: boolean;
  onSend: (prompt: string, box: BoxCoordinates | null, opts?: FloorplanSendOptions) => void;
  onSelectMessage: (messageNumber: number) => void;
  onRetryFailed: (messageId: string) => void;
  // Phase 2 — rebuild modal + uncertain hint
  imageId: string;
  /** Active version id + the raw storage path (not a URL) of the state the next edit applies to — both needed by the admin-only prompt inspector's dry-run preview. */
  versionId: string;
  currentStatePath: string;
  projectId: string;
  userId: string;
  sourceStoragePath: string;
  activeVersionNumber: number;
  rebuildSuggestion: IntentClassification | null;
  uncertainHint: IntentClassification | null;
  dismissUncertain: () => void;
  dismissRebuildSuggestion: () => void;
  forceEditRetry: () => void;
  onRebuildTriggered?: () => void;
}

export const FloorplanFeintuningPane: React.FC<FloorplanFeintuningPaneProps> = ({
  currentStateUrl,
  messages,
  activeMessageNumber,
  isGenerating,
  onSend,
  onSelectMessage,
  onRetryFailed,
  imageId,
  versionId,
  currentStatePath,
  projectId,
  userId,
  sourceStoragePath,
  activeVersionNumber,
  rebuildSuggestion,
  uncertainHint,
  dismissUncertain,
  dismissRebuildSuggestion,
  forceEditRetry,
  onRebuildTriggered,
}) => {
  const { isAdmin } = useAuth();
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [box, setBox] = useState<BoxCoordinates | null>(null);
  const [boxToolActive, setBoxToolActive] = useState(false);

  const [rebuildIntro, setRebuildIntro] = useState(REBUILD_REASON_INTRO_FALLBACK);
  useEffect(() => {
    let cancelled = false;
    supabase
      .from('prompt_templates')
      .select('content')
      .eq('key', 'floorplan_refinement_template_rebuild_reason_intro')
      .eq('is_active', true)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled && data?.content) setRebuildIntro(data.content as string);
      });
    return () => { cancelled = true; };
  }, []);

  const handleConfirmRebuild = async () => {
    if (!rebuildSuggestion) return;
    try {
      await triggerFloorplanRebuild({
        imageId,
        projectId,
        userId,
        sourceStoragePath,
        classification: rebuildSuggestion,
      });
      dismissRebuildSuggestion();
      onRebuildTriggered?.();
    } catch (e) {
      console.error('[feintuning-pane] rebuild failed:', e);
    }
  };

  return (
    <div className="flex-1 flex gap-3 min-h-0">
      {/* Canvas column */}
      <div className="flex-[2] flex flex-col gap-2 min-h-0">
        {/* Toolbar */}
        <div className="flex items-center gap-2 bg-card border border-border rounded-md px-3 py-1.5 text-xs shadow-sm">
          <span className="font-semibold text-foreground">Werkzeug:</span>
          <Button
            size="sm"
            variant={boxToolActive ? 'default' : 'outline'}
            onClick={() => setBoxToolActive((v) => !v)}
            className={`h-7 text-xs ${boxToolActive ? 'bg-primary hover:bg-primary/90 text-white' : ''}`}
            disabled={isGenerating}
          >
            <Square className="size-3.5 mr-1 stroke-[1.5]" />
            Bereich markieren
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!box || isGenerating}
            onClick={() => setBox(null)}
            className="h-7 text-xs"
          >
            <Trash2 className="size-3.5 mr-1 stroke-[1.5]" />
            Löschen
          </Button>
          <span className="ml-auto text-muted-foreground text-[10px]">
            Optional — geht auch ohne Markierung
          </span>
        </div>

        {/* Canvas (fixed-height container via flex + object-contain) */}
        <div className="relative flex-1 min-h-0 bg-white border border-border rounded-lg flex items-center justify-center p-4 overflow-hidden shadow-sm">
          <img
            ref={imageRef}
            src={currentStateUrl}
            alt="Aktueller Grundriss-Stand"
            className="max-w-full max-h-full object-contain select-none"
            draggable={false}
          />

          <BoxOverlay
            imageRef={imageRef}
            box={box}
            onBoxChange={setBox}
            disabled={!boxToolActive || isGenerating}
          />

          {isGenerating && (
            <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex flex-col items-center justify-center gap-3 z-50">
              <Loader2 className="size-10 animate-spin text-primary" />
              <p className="text-sm font-semibold text-foreground">
                Grundriss wird überarbeitet…
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Chat sidebar */}
      <div className="flex-1 flex flex-col min-h-0">
        {uncertainHint && (
          <FloorplanUncertainHint
            classification={uncertainHint}
            onDismiss={dismissUncertain}
          />
        )}

        <FloorplanMessageThread
          messages={messages}
          activeMessageNumber={activeMessageNumber}
          isGenerating={isGenerating}
          onSend={(prompt) => onSend(prompt, box)}
          onSelectMessage={onSelectMessage}
          onRetryFailed={onRetryFailed}
        />

        {/* Admin-only prompt inspector: dry-run the v2 classifier + prompt
            assembly (no image, no cost), tune the underlying prompts, and
            optionally trigger a real send with an exact finalPromptOverride.
            Non-admins never see this and never set an override. */}
        {isAdmin && (
          <div className="shrink-0 max-h-[50%] overflow-y-auto">
            <FloorplanRefineInspectorPanel
              imageId={imageId}
              versionId={versionId}
              currentStatePath={currentStatePath}
              sourceStoragePath={sourceStoragePath}
              projectId={projectId}
              userId={userId}
              box={box}
              onGenerateWithPrompt={(userPrompt, finalPrompt) =>
                onSend(userPrompt, box, { finalPromptOverride: finalPrompt })
              }
            />
          </div>
        )}
      </div>

      <FloorplanRebuildSuggestionModal
        open={rebuildSuggestion !== null}
        classification={rebuildSuggestion ?? ({} as IntentClassification)}
        currentVersion={activeVersionNumber}
        rebuildCostCredits={REBUILD_COST_CREDITS_FALLBACK}
        rebuildReasonIntro={rebuildIntro}
        onCancel={dismissRebuildSuggestion}
        onRebuild={handleConfirmRebuild}
        onForceEdit={forceEditRetry}
      />
    </div>
  );
};
